import React, { useCallback, useEffect, useState } from "react";
import { Card, Row, Col, Spinner, Button } from "react-bootstrap";
import {
  PeopleFill,
  BriefcaseFill,
  FileEarmarkTextFill,
  ArrowRepeat,
} from "react-bootstrap-icons";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

/* ===== RECHARTS ===== */
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const API_BASE = "http://34.206.193.218:4001/api/admin";
const DEFAULT_STATS = {
  totalUsers: 0,
  totalProjects: 0,
  totalBids: 0,
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(DEFAULT_STATS);
  const navigate = useNavigate();

  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");

  /* ================= FETCH DASHBOARD STATS ================= */
  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        setStats({
          ...DEFAULT_STATS,
          ...(res.data.data || {}),
        });
      }
    } catch {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" />
      </div>
    );
  }

  /* ===== CHART DATA ===== */
  const safeStats = stats || DEFAULT_STATS;

  const barData = [
    { name: "Users", value: safeStats.totalUsers },
    { name: "Projects", value: safeStats.totalProjects },
    { name: "Bids", value: safeStats.totalBids },
  ];

  const pieData = [
    { name: "Users", value: safeStats.totalUsers },
    { name: "Projects", value: safeStats.totalProjects },
    { name: "Bids", value: safeStats.totalBids },
  ];

  const COLORS = ["#6366f1", "#10b981", "#f59e0b"];

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ===== HEADER ===== */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h4 className="fw-bold mb-1">Owner Builder Dashboard</h4>
          <small className="text-muted">Overview of platform statistics</small>
        </div>
        <Button
          variant="outline-primary"
          className="d-flex align-items-center gap-2"
          onClick={fetchDashboardStats}
        >
          <ArrowRepeat />
          Refresh
        </Button>
      </div>

      {/* ===== KPI CARDS ===== */}
      <Row className="g-4 mb-4">
        <Col xl={4} md={6}>
          <Card
            className="kpi-card bg-indigo clickable"
            onClick={() => navigate("/users")}
          >
            <Card.Body>
              <h6>Total Users</h6>
              <h2>{safeStats.totalUsers}</h2>
              <PeopleFill size={30} />
            </Card.Body>
          </Card>
        </Col>

        <Col xl={4} md={6}>
          <Card
            className="kpi-card bg-green clickable"
            onClick={() => navigate("/projects")}
          >
            <Card.Body>
              <h6>Total Projects</h6>
              <h2>{safeStats.totalProjects}</h2>
              <BriefcaseFill size={30} />
            </Card.Body>
          </Card>
        </Col>

        <Col xl={4} md={6}>
          <Card
            className="kpi-card bg-orange clickable"
            onClick={() => navigate("/bids")}
          >
            <Card.Body>
              <h6>Total Bids</h6>
              <h2>{safeStats.totalBids}</h2>
              <FileEarmarkTextFill size={30} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ===== CHARTS ===== */}
      <Row className="g-4">
        {/* BAR CHART */}
        <Col lg={7}>
          <Card className="border-0 shadow-sm chart-card">
            <Card.Body>
              <h6 className="fw-semibold mb-3">Platform Overview</h6>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {barData.map((_, index) => (
                      <Cell key={index} fill={COLORS[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* PIE CHART */}
        <Col lg={5}>
          <Card className="border-0 shadow-sm chart-card">
            <Card.Body>
              <h6 className="fw-semibold mb-3">Distribution</h6>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    innerRadius={70}
                    outerRadius={110}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((_, index) => (
                      <Cell key={index} fill={COLORS[index]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ===== STYLES ===== */}
      <style>{`
        .chart-card{
        background-color: #ebebebff;
        }
        .kpi-card {
          border-radius: 16px;
          color: #fff;
          padding: 10px;
          border: none;
          position: relative;
          overflow: hidden;
        }
        .kpi-card h6 {
          opacity: 0.8;
          text-transform: uppercase;
          font-size: 0.8rem;
        }
        .kpi-card h2 {
          font-weight: 700;
          margin: 0;
        }
        .kpi-card svg {
          position: absolute;
          right: 20px;
          bottom: 30px;
          opacity: 1;
        }
        .bg-indigo {
          background: linear-gradient(135deg, #4f46e5, #6366f1);
        }
        .bg-green {
          background: linear-gradient(135deg, #059669, #10b981);
        }
        .bg-orange {
          background: linear-gradient(135deg, #f59e0b, #fbbf24);
        }
        .chart-card {
          border-radius: 16px;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
